import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Terminal, ShieldCheck, Sparkles, ArrowRight, Skull, Lock, Zap, X } from "lucide-react";
import api from "@/lib/api";
import { CATEGORY_STYLES } from "@/components/Badges";

const HERO_IMG =
  "https://images.unsplash.com/photo-1767817099805-d79e31fb968c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxjeWJlcnNlY3VyaXR5JTIwaGFja2VyJTIwdGVybWluYWwlMjBjb2RlJTIwZGFyayUyMHNjcmVlbnxlbnwwfHx8fDE3ODc0NTI1MzJ8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [stats, setStats] = useState(null);
  const [showCoin, setShowCoin] = useState(() => localStorage.getItem("ev_coin_dismissed") !== "1");

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const dismissCoin = () => {
    setShowCoin(false);
    localStorage.setItem("ev_coin_dismissed", "1");
  };

  const categories = Object.keys(CATEGORY_STYLES);

  return (
    <div>
      {showCoin && (
        <div
          data-testid="meme-coin-banner"
          className="relative z-20 flex items-center justify-center gap-3 px-4 py-2.5 border-b border-[#00ff66]/25 bg-[#00ff66]/[0.06] fade-up"
        >
          <img
            data-testid="meme-coin-logo"
            src="/vault-coin.png"
            alt="$VAULT coin"
            className="w-7 h-7 shrink-0 drop-shadow-[0_0_6px_rgba(0,255,102,0.5)]"
          />
          <p className="font-mono text-xs sm:text-sm text-slate-200 text-center">
            <span className="text-[#00ff66] font-bold">$VAULT</span> meme coin launches <span className="text-[#00ff66]">today</span> — right alongside the website.{" "}
            <span className="text-slate-500 hidden sm:inline">// to the moon, responsibly.</span>
          </p>
          <button
            data-testid="meme-coin-banner-close"
            onClick={dismissCoin}
            aria-label="Dismiss announcement"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#00ff66] transition-colors"
          >
            <X size={15} />
          </button>
        </div>
      )}
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={HERO_IMG} alt="terminal" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#080c14]/70 via-[#080c14]/90 to-[#080c14]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#00ff66] border border-[#00ff66]/30 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#00ff66] glow-pulse" />
              offensive knowledge // defensive mastery
            </div>

            <h1 className="font-mono text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Learn how attacks<br />
              <span className="neon-text">actually work.</span>
            </h1>
            <p className="mt-3 font-mono text-lg text-slate-500 cursor-blink">$ ./defend --against every-exploit</p>

            <p className="mt-6 text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl">
              A hands-on exploit database and defense academy. Break down real-world attack techniques
              step-by-step, then learn exactly how to protect yourself — with copy-ready secure code.
            </p>
            <p className="mt-3 font-mono text-xs text-[#00e5ff]/80 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] glow-pulse" />
              new exploits auto-synced from Exploit-DB + fresh AI breakdowns every day
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/database"
                data-testid="hero-browse-button"
                className="group flex items-center gap-2 bg-[#00ff66] text-[#04120a] font-mono font-bold px-6 py-3 rounded-md hover:shadow-[0_0_25px_rgba(0,255,102,0.4)] transition-all"
              >
                <Terminal size={18} /> Browse Exploits
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/ai-generator"
                data-testid="hero-ai-button"
                className="flex items-center gap-2 border border-[#00e5ff]/40 text-[#00e5ff] font-mono font-bold px-6 py-3 rounded-md hover:bg-[#00e5ff]/10 transition-all"
              >
                <Sparkles size={18} /> AI Tutorial Generator
              </Link>
            </div>
          </div>

          {/* stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14">
            {[
              { label: "Exploits", value: stats?.total ?? "—", icon: Skull, color: "#00ff66" },
              { label: "Critical", value: stats?.critical ?? "—", icon: Zap, color: "#ef4444" },
              { label: "Categories", value: 5, icon: Lock, color: "#00e5ff" },
              { label: "AI Generated", value: stats?.ai_generated ?? 0, icon: Sparkles, color: "#a855f7" },
            ].map((s) => (
              <div key={s.label} data-testid={`stat-${s.label.toLowerCase()}`} className="glass card-hover rounded-lg border border-slate-800 p-5">
                <s.icon size={20} style={{ color: s.color }} />
                <div className="mt-3 font-mono text-3xl font-extrabold" style={{ color: s.color }}>{s.value}</div>
                <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="text-[#00ff66]" size={22} />
          <h2 className="font-mono text-2xl font-bold">Attack Categories</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {categories.map((cat, i) => {
            const s = CATEGORY_STYLES[cat];
            return (
              <Link
                key={cat}
                to={`/database?category=${encodeURIComponent(cat)}`}
                data-testid={`home-category-${s.label.toLowerCase()}`}
                className="glass card-hover rounded-lg border border-slate-800 p-5 fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="w-10 h-10 rounded-md flex items-center justify-center mb-3"
                  style={{ background: `${s.color}15`, border: `1px solid ${s.ring}` }}>
                  <Terminal size={18} style={{ color: s.color }} />
                </div>
                <div className="font-mono font-bold text-sm" style={{ color: s.color }}>{s.label}</div>
                <div className="text-xs text-slate-500 mt-1">{cat}</div>
                <div className="font-mono text-xs text-slate-600 mt-3">
                  {stats?.by_category?.[cat] ?? "—"} entries →
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 py-10 mt-6 border-t border-slate-800/60">
        <p className="font-mono text-xs text-slate-600">
          // For educational and defensive purposes only. Test only on systems you own or are authorized to assess.
        </p>
      </footer>
    </div>
  );
}
